import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Box, Button, Card, CardContent, Chip, Grid, IconButton, Stack, TextField, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useBlocker, useLocation } from 'react-router';
import { InferType } from 'yup';

import { WarningRounded } from '@mui/icons-material';
import {
  EmptyContent,
  FormProvider,
  FullPageSpinner,
  RHFDatePicker,
  RHFRadioGroup,
  RHFReactNumberFormat,
  RHFTextField,
  enqueueSnackbar,
  fNumber,
  getCurrencySymbol,
  useBoolean,
  useDefaultEnumsStore,
  useDirtyFieldsStore,
  useFactFindConfigStore,
  useFactFindErrorStore,
  useFactFindProcessStore,
  useUserAccessStore,
  useUserDetailsStore,
} from '@zeta-core';
import { PlusIcon } from '@zeta-core/assets';
import {
  AIInteractionService,
  IFactFindProcessSectionType,
  IFactFindProcessStatus,
  IPatchAIInteractionSectionRequest,
  InteractionSubSectionType,
  InvestorService,
} from '@zeta-services';
import {
  FactFindType,
  ICaptureFactFindIncomeRequest,
  IClientCardItem,
  IEmptyRequestResponse,
  IFFSummaryCalculateRequest,
  IFFSummaryCalculateResponse,
  IFactFindEntity,
  IViewFactFindIncomeResponse,
  IViewFactFindRequest,
} from '@zeta-types';
import { ConflictsDialog } from '../../../components/conflicts-dialog';
import { getClientFeatureAccess } from '../../../helpers/access-control-helper';
import AllTranscripts from '../all-transcripts';
import { getLabel, yesNoObject } from '../common/commons';
import FactFindHeading from '../common/fact-find-heading';
import ConfirmNavigationDialog from '../components/confirmation-dialog';
import ErrorDialog from '../components/error-dialog';
import { ErrorSection } from '../components/error-section';
import SaveDialog from '../components/save-dialog';
import SimpleFieldsTable from '../components/simple-fields-table';
import SuccessDialog from '../components/success-dialog';
import SummaryTabComponent from '../components/summary-tab-component';
import FactFindFooter from '../fact-find-layout/fact-find-footer';
import { useSourceControlContext } from '../fact-find-layout/sourcecontrol-context-component';
import Transcript from '../transcript';
import { createIncomePayload, incomeDefault, incomeDefaultValues, incomeValidationSchema, showField } from './form-helpers';

interface IIncomeEntry {
  id?: string;
  incomeType?: string;
  incomeSource?: string;
  amount?: string;
  frequency?: string;
  startDate?: string;
  endDate?: string;
  taxable?: string;
  taxAmount?: string;
  taxWithheld?: string;
  show?: boolean;
}

type TabType = 'client' | 'partner' | 'joint' | 'summary';

export const Income = ({
  isClient,
  isManagedClient,
  from,
  refetch,
  clientData,
  conflictData,
  summary,
  updatedClientPartnerDetails,
}: {
  isClient?: boolean;
  isManagedClient?: boolean;
  from: FactFindType;
  refetch?: () => void;
}) => {
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const location = useLocation();
  const { accessibleEntityID } = useUserDetailsStore((state) => state.user);
  let state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  if (isClient) {
    state = { clientData: { clientID: accessibleEntityID } };
  }
  const {
    data: sourceControlData,
    mutateAsync: sourceControlMutateAsync,
    isPending: sourceControlPending,
    clientPartnerDetails,
  } = useSourceControlContext() || {};
  const isTranscript = useBoolean();
  const isAllTranscripts = useBoolean();
  const [currentTab, setCurrentTab] = useState<TabType>('summary');

  const [loading, setLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [conflictsDialog, setConflictsDialog] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const updateDirtyFieldsState = useDirtyFieldsStore((state) => state.updateDirtyFieldsState);
  const [confirmNavigationModal, setConfirmNavigationModal] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const theme = useTheme();

  const ffConfig = useFactFindConfigStore((state) => state?.factFindConfigDetails?.individualFactFindSectionConfig?.income);
  const ffErrors = useFactFindErrorStore((state) => state?.ffErrors);
  const updateFFErrorStore = useFactFindErrorStore((state) => state?.updateFFErrorStore);
  const sectionsProcessDetails = useFactFindProcessStore((state) => state.sections);
  const currentSection = sectionsProcessDetails?.find((section) => section.section_name === IFactFindProcessSectionType.income)?.status;
  const planningStatus = location?.state?.clientData?.planningStatus;
  const readOnly = getClientFeatureAccess(userAccess?.access, 'Advisor-Client-FF', planningStatus).isReadOnly;
  const isReadOnly =
    from === FactFindType.PARA_PLANNER ||
    currentSection === IFactFindProcessStatus.pending ||
    currentSection === IFactFindProcessStatus.started ||
    isClient ||
    readOnly;

  const fieldErrors = ffErrors?.find((error) => error.section === InteractionSubSectionType.income_details)?.missing_fields;
  type incomeValidationSchemaType = InferType<typeof incomeValidationSchema>;
  const defaultEnums = useDefaultEnumsStore((state) => state.defaultEnums);
  const frequencyOptions =
    defaultEnums?.frequencyType
      ?.filter((e) => e.frequencyTypeID === '1002' || e.frequencyTypeID === '1004')
      .map((e) => {
        return { label: e.frequencyType, value: e.frequencyType };
      }) ?? [];

  const methods = useForm<incomeValidationSchemaType>({
    defaultValues: incomeDefaultValues(ffConfig),
    resolver: yupResolver(incomeValidationSchema),
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const { reset } = methods;

  const incomeFieldArrayClient = useFieldArray({ name: 'client', control: methods.control });
  const incomeFieldArrayPartner = useFieldArray({ name: 'partner', control: methods.control });
  const incomeFieldArrayJoint = useFieldArray({ name: 'joint', control: methods.control });

  const createIncomeEntry = (details: IIncomeEntry) => {
    return {
      id: details.id || '',
      detail: details.incomeType || '',
      incomeSource: details.incomeSource || '',
      amount: details.amount ? details.amount?.toString() : null,
      frequency: details.frequency || '',
      startDate: details.startDate || '',
      endDate: details.endDate || '',
      isTaxable: details.taxable ? 'YES' : 'NO',
      taxAmount: details.taxWithheld ? details.taxWithheld?.toString() : null,
      show: showField(ffConfig, details?.incomeType?.split(' ').join().toLowerCase()) ?? true,
    };
  };

  const transformIncomeData = (incomeDetails: any): IIncomeEntry[] => {
    const transformedData = incomeDetails?.map((e) => createIncomeEntry(e));
    return transformedData?.length > 0 ? transformedData : incomeDefault(ffConfig?.fields);
  };

  const [summaryData, setSummaryData] = useState({});

  const calculateSummary = useMutation({
    mutationFn: (payload: IFFSummaryCalculateRequest) => InvestorService().ffSummaryCalculate(payload),

    onSuccess: (response: AxiosResponse<IFFSummaryCalculateResponse>) => {
      setSummaryData(response.data.response);
    },
  });

  useEffect(() => {
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      calculateSummary.mutateAsync({ clientID: state.clientData.clientID, sectionType: 'Income' });
    }
    if (from === FactFindType.AI) {
      setSummaryData(summary);
    }
  }, [from, calculateSummary.mutateAsync, state.clientData.clientID, summary]);

  const {
    mutateAsync,
    isPending,
    data: incomeData,
  } = useMutation({
    mutationFn: (payload: IViewFactFindRequest) => InvestorService().viewIncome(payload),

    onSuccess: (response: AxiosResponse<IViewFactFindIncomeResponse>) => {
      const { data: responseData } = response;

      const clientTransformedData = transformIncomeData(responseData?.response?.income?.clientIncome);
      const partnerTransformedData = transformIncomeData(responseData?.response?.income?.partnerIncome);
      const jointTransformedData = transformIncomeData(responseData?.response?.income?.jointIncome);

      const incomes: incomeValidationSchemaType = { client: clientTransformedData, partner: partnerTransformedData, joint: jointTransformedData };

      methods.reset(incomes);
    },
  });

  useEffect(() => {
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      mutateAsync({ clientID: state.clientData.clientID, forExternalUse: true });
    }
    if (from === FactFindType.AI) {
      const responseData = clientData;
      const clientTransformedData = transformIncomeData(responseData?.response?.income?.clientIncome);
      const partnerTransformedData = transformIncomeData(responseData?.response?.income?.partnerIncome);
      const jointTransformedData = transformIncomeData(responseData?.response?.income?.jointIncome);

      const incomes: incomeValidationSchemaType = { client: clientTransformedData, partner: partnerTransformedData, joint: jointTransformedData };

      reset(incomes);
    }
  }, [mutateAsync, from, state.clientData.clientID, clientData, reset]);

  const captureIncome = useMutation({
    mutationFn: (payload: ICaptureFactFindIncomeRequest) => InvestorService().captureIncome(payload),
    onSuccess: (response: AxiosResponse<IEmptyRequestResponse>) => {
      if (response.status === 200) {
        setLoading(false);
        enqueueSnackbar('Income details saved successfully', { variant: 'success' });
        mutateAsync({ clientID: state.clientData.clientID });
        sourceControlMutateAsync({ clientID: state.clientData.clientID });
        calculateSummary.mutateAsync({ clientID: state.clientData.clientID, sectionType: 'Income' });
      }
    },
    onError: () => {
      setLoading(false);
      setIsError(true);
      setTimeout(() => {
        setIsError(false);
      }, 5000);
    },
  });

  const captureIncomeAI = useMutation({
    mutationFn: (payload: IPatchAIInteractionSectionRequest) => AIInteractionService().patchClientData(payload),
    onSuccess: (response: AxiosResponse<IEmptyRequestResponse>) => {
      if (response.status === 200) {
        setLoading(false);
        refetch?.();
        enqueueSnackbar('Income details saved successfully', { variant: 'success' });
        sourceControlMutateAsync({ clientID: state.clientData.clientID });
        updateFFErrorStore({ ffErrors: null });
      }
    },
    onError: () => {
      setLoading(false);
      setIsError(true);
      setTimeout(() => {
        setIsError(false);
      }, 5000);
    },
  });

  const onSubmit = (data: incomeValidationSchemaType) => {
    const transformedPayload = createIncomePayload(data);
    setLoading(true);
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      captureIncome.mutateAsync({ ...transformedPayload, clientID: state.clientData.clientID });
    }
    if (from === FactFindType.AI) {
      const payloadAI = {
        section: InteractionSubSectionType.income_details,
        data: { response: { income: transformedPayload } },
        client_id: state.clientData.clientID,
      };
      captureIncomeAI.mutate(payloadAI);
    }
  };

  const calculateTotal = (field: 'monthly' | 'annually') => {
    const values = methods.watch(currentTab);
    return values?.reduce((total: number, item: any) => (item?.frequency?.toLowerCase() === field ? total + (parseFloat(item.amount) || 0) : total), 0) || 0;
  };

  const totalAnnuallIncome = calculateTotal('monthly') * 12 + calculateTotal('annually');

  const isDirtyInPage = methods.formState.isDirty;

  useEffect(() => {
    if (isDirtyInPage) {
      updateDirtyFieldsState(true);
    }
  }, [isDirtyInPage, updateDirtyFieldsState]);

  const blocker = useBlocker(({ nextLocation, currentLocation }) => {
    return isDirtyInPage && currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    if (blocker.state === 'blocked' && showPrompt) {
      setConfirmNavigationModal(true);
    } else {
      blocker?.proceed?.();
    }
  }, [blocker, showPrompt]);

  const handleDiscardAndProceed = () => {
    setConfirmNavigationModal(false);
    blocker?.proceed?.();
    setShowPrompt(false);
    updateDirtyFieldsState(false);
  };

  const handleCloseNavigationModal = () => {
    setConfirmNavigationModal(false);
    blocker?.reset?.();
    setShowPrompt(true);
  };

  const handleChangeTab = (event: SyntheticEvent, value: TabType) => {
    setCurrentTab(value);
  };

  const TABLE_DETAILS = [
    { label: 'Details', name: 'detail', component: RHFTextField, minWidth: 150, width: 150, show: true },
    { label: 'Income Source', name: 'incomeSource', component: RHFTextField, minWidth: 150, width: 150, show: true },
    {
      label: 'Amount',
      name: 'amount',
      component: RHFReactNumberFormat,
      decimalScale: 2,
      prefix: true,
      thousandSeparator: true,
      fixedDecimalScale: true,
      textAlign: 'right',
      minWidth: 150,
      width: 150,
      show: true,
    },
    { label: 'Frequency', name: 'frequency', component: RHFRadioGroup, options: frequencyOptions, row: true, minWidth: 250, width: 250, show: true },
    { label: 'Start Date', name: 'startDate', component: RHFDatePicker, minWidth: 150, width: 150, show: true },
    { label: 'End Date', name: 'endDate', component: RHFDatePicker, minWidth: 150, width: 150, show: true },
    { label: 'Is Taxable?', name: 'isTaxable', component: RHFRadioGroup, options: yesNoObject, row: true, minWidth: 200, width: 200, show: true },
    {
      label: 'Tax Amount',
      name: 'taxAmount',
      component: RHFReactNumberFormat,
      decimalScale: 2,
      prefix: true,
      thousandSeparator: true,
      fixedDecimalScale: true,
      textAlign: 'right',
      minWidth: 150,
      width: 150,
      show: true,
    },
    { label: '', name: 'action', component: RHFReactNumberFormat, minWidth: 50, width: 50, show: true },
  ];

  const fetchFieldArray = (tab: TabType) => {
    switch (tab) {
      case 'client':
        return incomeFieldArrayClient;
      case 'partner':
        return incomeFieldArrayPartner;
      case 'joint':
        return incomeFieldArrayJoint;
    }
  };

  const tabSections: ('summary' | 'joint' | 'client' | 'partner')[] = ['summary', 'client', 'partner', 'joint'];

  const getClientsOptions = tabSections.map((type) => ({
    label: getLabel(
      { value: type, label: type.charAt(0).toUpperCase() + type.slice(1) },
      [FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from) && !isClient ? clientPartnerDetails : updatedClientPartnerDetails
    ),
    value: type,
  }));

  return (
    <Box mb={5 * 2}>
      {isAllTranscripts.value && <AllTranscripts isAllTranscripts={isAllTranscripts} />}
      {isTranscript.value && <Transcript section="Income" isTranscript={isTranscript} />}
      {!isTranscript.value && !isAllTranscripts.value && (
        <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
          {isPending || sourceControlPending ? (
            <FullPageSpinner />
          ) : (
            <>
              <Stack direction="row" alignItems="center">
                <FactFindHeading
                  sectionName="Income"
                  isAllTranscripts={isAllTranscripts}
                  isTranscript={isTranscript}
                  hideButton={sourceControlData?.dataSourceMap?.Income === 'ManualInput'}
                  hideAllButton={isClient || [FactFindType.AI, FactFindType.PARA_PLANNER].includes(from) || isManagedClient}
                />

                {conflictData?.length > 0 && (
                  <Tooltip title="Click here to view all the conflicts">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => {
                        setConflictsDialog(true);
                      }}
                      sx={{ marginTop: '-12px' }}
                    >
                      <WarningRounded />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {fieldErrors?.length > 0 && <ErrorSection errors={fieldErrors} />}

              <Stack sx={{ flexDirection: 'row', alignItems: 'center', mb: 2.5 }}>
                <Autocomplete
                  options={
                    from === FactFindType.PARA_PLANNER ? getClientsOptions.filter((e) => !['Client', 'Partner', 'Joint'].includes(e.label)) : getClientsOptions
                  }
                  id="incomeDropdown"
                  value={getClientsOptions.find((option) => option.value === currentTab)?.label ?? ''}
                  sx={{ width: 300 }}
                  onChange={(e, v) => handleChangeTab(e, v?.value)}
                  getOptionLabel={(option) => option?.label || option}
                  renderInput={(params) => <TextField {...params} />}
                  disableClearable={true}
                />
              </Stack>

              <Stack spacing={1.5} marginTop={2}>
                {currentTab === 'summary' && (
                  <SummaryTabComponent
                    section="Income"
                    clientPartnerDetails={from === FactFindType.AI ? updatedClientPartnerDetails : clientPartnerDetails}
                    summaryData={summaryData}
                  />
                )}

                {['client', 'partner', 'joint'].map(
                  (tab) =>
                    currentTab === tab && (
                      <React.Fragment key={tab}>
                        {isReadOnly && Object.keys(incomeData?.data?.response?.income ?? {})?.length === 0 && (
                          <Card>
                            <Stack direction="row" justifyContent="center" alignItems="center" height="60vh">
                              <EmptyContent description="No income information has been captured as part of the fact-finding process." />
                            </Stack>
                          </Card>
                        )}

                        {((isReadOnly && Object.keys(incomeData?.data?.response?.income ?? {})?.length > 0 && fetchFieldArray(tab).fields?.length > 0) ||
                          !isReadOnly) && (
                          <SimpleFieldsTable
                            fieldArray={fetchFieldArray(tab).fields}
                            currentTab={currentTab}
                            onRemove={fetchFieldArray(tab).remove}
                            tableDetails={TABLE_DETAILS}
                            isClient={isClient}
                            isReadOnly={isReadOnly}
                          />
                        )}
                        {isClient || isReadOnly ? null : (
                          <Stack flexDirection="row" marginTop={1}>
                            <Button
                              variant="text"
                              id="add-income"
                              color="primary"
                              sx={{ marginLeft: 1 }}
                              size="small"
                              startIcon={<PlusIcon />}
                              onClick={() => {
                                const appendFunction = fetchFieldArray(tab).append;

                                appendFunction({
                                  detail: '',
                                  incomeSource: '',
                                  amount: 0,
                                  startDate: '',
                                  endDate: '',
                                  isTaxable: '',
                                  taxAmount: 0,
                                  show: true,
                                });
                              }}
                            >
                              Add Other Income
                            </Button>
                          </Stack>
                        )}
                      </React.Fragment>
                    )
                )}
              </Stack>

              {currentTab !== 'summary' && !isReadOnly && Object.keys(incomeData?.data?.response?.income ?? {})?.length === 0 && (
                <Grid container spacing={2} mt={1}>
                  <Grid
                    size={{
                      lg: 6,
                      md: 6,
                      sm: 6
                    }}>
                    <Card sx={{ background: alpha(theme.palette.grey[100], 1) }}>
                      <CardContent
                        component={Stack}
                        gap={2}
                        flexDirection="row"
                        justifyContent="space-between"
                        sx={{ p: 2, pb: '8px !important', pt: '8px !important' }}
                        alignItems="center"
                      >
                        <Typography variant="subtitle1" id="total-income-monthly">
                          Total Income Monthly
                        </Typography>

                        <Chip
                          label={
                            <Typography variant="chipLabel" id="gbp-value">
                              {getCurrencySymbol('GBP')} {fNumber(totalAnnuallIncome / 12)}
                            </Typography>
                          }
                          color="success"
                          variant="soft"
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid
                    size={{
                      lg: 6,
                      md: 6,
                      sm: 6
                    }}>
                    <Card sx={{ background: alpha(theme.palette.grey[100], 1) }}>
                      <CardContent
                        component={Stack}
                        gap={2}
                        flexDirection="row"
                        justifyContent="space-between"
                        sx={{ p: 2, pb: '8px !important', pt: '8px !important' }}
                        alignItems="center"
                      >
                        <Typography variant="subtitle1" id="total-income-annually">
                          Total Income Annually
                        </Typography>

                        <Chip
                          label={
                            <Typography variant="chipLabel" id="gbp-value">
                              {getCurrencySymbol('GBP')} {fNumber(totalAnnuallIncome)}
                            </Typography>
                          }
                          color="success"
                          variant="soft"
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {isClient ? null : (
            <>
              {[FactFindType.FACT_FIND, FactFindType.AI].includes(from) && (
                <FactFindFooter isManagedClient={isManagedClient} from={from} hideNextStep={from === FactFindType.AI} hideSave={isReadOnly} />
              )}
              <SaveDialog open={loading} />
              <SuccessDialog open={isSuccess} />
              <ErrorDialog open={isError} />
              <ConfirmNavigationDialog open={confirmNavigationModal} handleClose={handleCloseNavigationModal} data={{ handleDiscardAndProceed, blocker }} />
              <ConflictsDialog
                open={conflictsDialog}
                handleClose={() => setConflictsDialog(false)}
                tableData={conflictData || []}
                isPending={false}
                title={'Income'}
                refetch={refetch}
              />
            </>
          )}
        </FormProvider>
      )}
    </Box>
  );
};
